package com.iregained.site.components.sections.resources.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.resources.styles.ResourcesCtaButtonStyle
import com.iregained.site.components.widgets.SharedCtaActionLink
import com.iregained.site.components.widgets.SharedCtaWidget
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.scale
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.s
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Lens Focus Reveal
@Composable
private fun Modifier.lensFocusReveal(isVisible: Boolean): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 1.05f)
        .styleModifier { property("filter", if (isVisible) "blur(0px)" else "blur(10px)") }.transition(
            Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut),
            Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut),
            Transition.of("filter", 0.8.s, TransitionTimingFunction.EaseOut)
        )
}

@Composable
fun ResourcesCtaSection(data: ResourcesCtaData = defaultResourcesCtaData) {
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "resources-brochure-download"
    var isVisible by remember { mutableStateOf(false) }

    if (data.headline.isNotBlank()) {
        Div(attrs = Modifier.fillMaxWidth().toAttrs {
            ref { element ->
                val observer = IntersectionObserver { entries, obs ->
                    entries.forEach {
                        if (it.isIntersecting) {
                            isVisible = true; obs.unobserve(element)
                        }
                    }
                }
                observer.observe(element); onDispose { observer.disconnect() }
            }
        }) {
            Box(modifier = Modifier.fillMaxWidth().lensFocusReveal(isVisible)) {
                SharedCtaWidget(
                    headline = data.headline, description = data.subheadline
                ) {
                    SharedCtaActionLink(
                        text = data.buttonText,
                        path = data.buttonLink,
                        styleModifier = ResourcesCtaButtonStyle.toModifier()
                    )
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL
// ==========================================
data class ResourcesCtaData(
    val sectionId: String? = null,
    val headline: String = "Looking for specific documentation?",
    val subheadline: String = "Whether you are a clinician reviewing our clinical trial outcomes or a patient looking for setup guides, our support team is ready to assist you.",
    val buttonText: String = "Contact Support & HQ",
    val buttonLink: String = "/contact"
)

val defaultResourcesCtaData = ResourcesCtaData()